import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarDays, Clock3, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { waLink } from "@/lib/site";

export default function BlogPost() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setBlog(null); setNotFound(false);
    api.get(`/blogs/${slug}`)
      .then((r) => { setBlog(r.data); document.title = `${r.data.title} — click2trip blog`; })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return <div className="pt-40 pb-32 text-center"><h1 className="font-display text-3xl font-black">Article not found</h1></div>;
  if (!blog) return <div className="pt-40 pb-32 max-w-3xl mx-auto px-6 animate-pulse"><div className="h-64 bg-sand rounded-3xl" /></div>;

  return (
    <div className="pt-32 pb-24" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-4 sm:px-6">
        <Reveal>
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-ink/50 hover:text-sun mb-8 transition-colors" data-testid="back-to-blog-link">
            <ArrowLeft size={14} /> All guides
          </Link>
          <p className="text-xs uppercase tracking-[0.25em] text-sun mb-4">{blog.tag}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-tight mb-5" data-testid="blog-title">{blog.title}</h1>
          <div className="flex items-center gap-5 text-sm text-ink/50 mb-10">
            <span className="flex items-center gap-1.5"><CalendarDays size={14} />{blog.date}</span>
            <span className="flex items-center gap-1.5"><Clock3 size={14} />{blog.read_time}</span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="overflow-hidden rounded-3xl aspect-[16/9] mb-12">
            <img src={blog.image} alt={blog.title} className="w-full h-full object-cover" />
          </div>
        </Reveal>
        <div className="space-y-6" data-testid="blog-content">
          {blog.content.map((para, i) => (
            <Reveal key={i} delay={0.05 * i}>
              {i === 0 ? (
                <p className="text-xl font-light leading-relaxed text-ink first-letter:font-display first-letter:text-6xl first-letter:font-black first-letter:text-sun first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]">{para}</p>
              ) : (
                <p className="text-ink/70 leading-relaxed">{para}</p>
              )}
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-14">
          <div className="bg-ink text-bone rounded-3xl p-8 grain relative overflow-hidden flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="font-display text-2xl font-black mb-1">Inspired? Let's build your trip.</p>
              <p className="text-bone/60 text-sm">Free consultation with a travel expert.</p>
            </div>
            <a href={waLink(`Hi click2trip! I read "${blog.title}" and want to plan a trip.`)} target="_blank" rel="noreferrer"
              data-testid="blog-cta-btn"
              className="bg-sun text-bone font-semibold px-7 py-3.5 rounded-full hover:scale-105 transition-transform shrink-0">
              Plan My Trip
            </a>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
